/**
 * IndexedDB service for offline trip storage
 * Provides persistent local storage for trips, days, and todos
 */

const DB_NAME = 'travaili_offline';
const DB_VERSION = 1;
const STORES = {
  TRIPS: 'trips',
  PENDING_CHANGES: 'pendingChanges',
  SYNC_STATE: 'syncState'
};

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store for trips
        if (!db.objectStoreNames.contains(STORES.TRIPS)) {
          const tripStore = db.createObjectStore(STORES.TRIPS, { keyPath: 'id' });
          tripStore.createIndex('userId', 'userId', { unique: false });
          tripStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        // Store for pending changes (offline modifications)
        if (!db.objectStoreNames.contains(STORES.PENDING_CHANGES)) {
          const changesStore = db.createObjectStore(STORES.PENDING_CHANGES, {
            keyPath: 'id',
            autoIncrement: true
          });
          changesStore.createIndex('timestamp', 'timestamp', { unique: false });
          changesStore.createIndex('synced', 'synced', { unique: false });
        }

        // Store for sync state
        if (!db.objectStoreNames.contains(STORES.SYNC_STATE)) {
          db.createObjectStore(STORES.SYNC_STATE, { keyPath: 'key' });
        }

        console.log('IndexedDB schema created');
      };
    });

    return this.initPromise;
  }

  /**
   * Save a trip to local storage
   */
  async saveTrip(trip) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.TRIPS], 'readwrite');
      const store = transaction.objectStore(STORES.TRIPS);

      const tripToSave = {
        ...trip,
        _offlineUpdatedAt: Date.now()
      };

      const request = store.put(tripToSave);

      request.onsuccess = () => resolve(tripToSave);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a single trip by ID
   */
  async getTrip(tripId) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.TRIPS], 'readonly');
      const store = transaction.objectStore(STORES.TRIPS);
      const request = store.get(tripId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all trips for a user
   */
  async getUserTrips(userId) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.TRIPS], 'readonly');
      const store = transaction.objectStore(STORES.TRIPS);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const trips = request.result || [];
        // Sort by updatedAt (most recent first)
        trips.sort((a, b) => {
          const timeA = a.updatedAt?.toMillis?.() || a._offlineUpdatedAt || 0;
          const timeB = b.updatedAt?.toMillis?.() || b._offlineUpdatedAt || 0;
          return timeB - timeA;
        });
        resolve(trips);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete a trip from local storage
   */
  async deleteTrip(tripId) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.TRIPS], 'readwrite');
      const store = transaction.objectStore(STORES.TRIPS);
      const request = store.delete(tripId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save a pending change (for offline modifications)
   */
  async savePendingChange(change) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);

      const changeRecord = {
        ...change,
        timestamp: Date.now(),
        synced: false
      };

      const request = store.add(changeRecord);

      request.onsuccess = () => resolve({ ...changeRecord, id: request.result });
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all pending changes that haven't been synced
   */
  async getPendingChanges() {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_CHANGES], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      const request = store.getAll();

      request.onsuccess = () => {
        const allChanges = request.result || [];
        // Filter for unsynced changes only
        const changes = allChanges.filter(change => !change.synced);
        // Sort by timestamp (oldest first)
        changes.sort((a, b) => a.timestamp - b.timestamp);
        resolve(changes);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Mark a pending change as synced
   */
  async markChangeSynced(changeId) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      const getRequest = store.get(changeId);

      getRequest.onsuccess = () => {
        const change = getRequest.result;
        if (change) {
          change.synced = true;
          change.syncedAt = Date.now();
          const putRequest = store.put(change);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Delete a synced pending change
   */
  async deletePendingChange(changeId) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      const request = store.delete(changeId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all synced pending changes
   */
  async clearSyncedChanges() {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_CHANGES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_CHANGES);
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          // Only delete if synced is true
          if (cursor.value.synced === true) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save sync state
   */
  async setSyncState(key, value) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_STATE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_STATE);
      const request = store.put({ key, value, updatedAt: Date.now() });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get sync state
   */
  async getSyncState(key) {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_STATE], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_STATE);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data (for logout)
   */
  async clearAll() {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        [STORES.TRIPS, STORES.PENDING_CHANGES, STORES.SYNC_STATE],
        'readwrite'
      );

      let cleared = 0;
      const stores = [STORES.TRIPS, STORES.PENDING_CHANGES, STORES.SYNC_STATE];

      stores.forEach(storeName => {
        const request = transaction.objectStore(storeName).clear();
        request.onsuccess = () => {
          cleared++;
          if (cleared === stores.length) {
            resolve();
          }
        };
      });

      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get storage statistics
   */
  async getStats() {
    const db = await this.init();
    return new Promise((resolve, reject) => {
      const stats = {};
      const transaction = db.transaction(
        [STORES.TRIPS, STORES.PENDING_CHANGES],
        'readonly'
      );

      // Count trips
      const tripRequest = transaction.objectStore(STORES.TRIPS).count();
      tripRequest.onsuccess = () => {
        stats.trips = tripRequest.result;
      };

      // Count pending changes
      const changesRequest = transaction.objectStore(STORES.PENDING_CHANGES).count();
      changesRequest.onsuccess = () => {
        stats.pendingChanges = changesRequest.result;
      };

      transaction.oncomplete = () => resolve(stats);
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();

// Export class for testing
export { OfflineStorageService };
